import { useState, type ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { Menu, X } from "lucide-react";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

export interface SeccionItem {
  id: string;
  label: string;
}

interface Props {
  titulo: string;
  subtitulo: string;
  icono: ReactNode;
  rutaActiva?: "/operativo";
  secciones: SeccionItem[];
  activa: string;
  onActiva: (id: string) => void;
  children: ReactNode;
  pie: string;
}

const RUTAS = [{ to: "/operativo", label: "Simulador Operativo" }] as const;

export function AppShell({
  titulo,
  subtitulo,
  icono,
  rutaActiva,
  secciones,
  activa,
  onActiva,
  children,
  pie,
}: Props) {
  const [abierto, setAbierto] = useState(false);
  const seccionActual = secciones.find((s) => s.id === activa)?.label ?? "";

  return (
    <div className="flex min-h-screen flex-col bg-background">
      {/* Header compacto y sticky */}
      <header className="no-print sticky top-0 z-40 border-b bg-navy shadow-sm">
        <div className="mx-auto grid max-w-7xl grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-3 px-4 py-3 sm:px-6 lg:py-5">
          <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-primary-foreground/10 lg:h-10 lg:w-10">
            {icono}
          </span>
          <div className="min-w-0">
            <p className="truncate text-[11px] font-medium uppercase tracking-wide text-primary-foreground/60 lg:hidden">
              {titulo}
            </p>
            <h1 className="truncate text-base font-semibold tracking-tight text-primary-foreground lg:text-xl">
              <span className="lg:hidden">{seccionActual}</span>
              <span className="hidden lg:inline">{titulo}</span>
            </h1>
            <p className="hidden text-xs text-primary-foreground/70 lg:block">{subtitulo}</p>
          </div>

          {/* Nav de rutas en desktop */}
          <nav className="hidden gap-2 text-sm lg:flex">
            {RUTAS.map((r) => (
              <Link
                key={r.to}
                to={r.to}
                className={
                  r.to === rutaActiva
                    ? "rounded-md bg-primary-foreground/15 px-3 py-1.5 font-medium text-primary-foreground"
                    : "rounded-md px-3 py-1.5 text-primary-foreground/80 transition-colors hover:bg-primary-foreground/10"
                }
              >
                {r.label}
              </Link>
            ))}
          </nav>

          {/* Menú hamburguesa en móvil */}
          <Sheet open={abierto} onOpenChange={setAbierto}>
            <SheetTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                aria-label="Abrir menú de secciones"
                className="h-11 w-11 shrink-0 text-primary-foreground hover:bg-primary-foreground/10 lg:hidden"
              >
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[85vw] max-w-sm overflow-y-auto p-0">
              <div className="flex items-center justify-between border-b bg-navy px-4 py-3">
                <SheetTitle className="text-sm font-semibold text-primary-foreground">
                  Secciones
                </SheetTitle>
                <button
                  type="button"
                  aria-label="Cerrar menú"
                  onClick={() => setAbierto(false)}
                  className="grid h-9 w-9 place-items-center rounded-md text-primary-foreground/80"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
              <nav className="space-y-1 p-3">
                {secciones.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => {
                      onActiva(s.id);
                      setAbierto(false);
                    }}
                    className={`flex w-full items-center rounded-md px-3 py-3 text-left text-sm ${
                      s.id === activa ? "bg-navy text-primary-foreground" : "bg-surface text-navy"
                    }`}
                  >
                    {s.label}
                  </button>
                ))}
              </nav>
              <div className="border-t p-3">
                <p className="px-1 pb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Otros módulos
                </p>
                {RUTAS.map((r) => (
                  <Link
                    key={r.to}
                    to={r.to}
                    onClick={() => setAbierto(false)}
                    className={`flex items-center rounded-md px-3 py-3 text-sm ${
                      r.to === rutaActiva ? "font-semibold text-navy" : "text-muted-foreground"
                    }`}
                  >
                    {r.label}
                  </Link>
                ))}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </header>

      {/* Tabs completos solo en desktop */}
      <div className="no-print mx-auto hidden w-full max-w-7xl px-4 pt-6 sm:px-6 lg:block">
        <Tabs value={activa} onValueChange={onActiva}>
          <TabsList className="h-auto w-full flex-wrap justify-start gap-1 bg-secondary p-1">
            {secciones.map((s) => (
              <TabsTrigger key={s.id} value={s.id}>
                {s.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      </div>

      <main className="mx-auto w-full max-w-7xl flex-1 px-4 py-6 pb-24 sm:px-6 lg:pb-10">
        {children}
      </main>

      {/* Barra inferior fija en móvil con las secciones principales */}
      <nav className="no-print fixed bottom-0 left-0 right-0 z-40 border-t bg-card/95 backdrop-blur lg:hidden">
        <div className="flex overflow-x-auto">
          {secciones.map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => onActiva(s.id)}
              className={`min-w-[33%] flex-1 whitespace-nowrap px-3 py-3 text-xs font-medium ${
                s.id === activa
                  ? "border-t-2 border-navy bg-navy-soft text-navy"
                  : "text-muted-foreground"
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>
      </nav>

      <footer className="no-print border-t bg-surface">
        <div className="mx-auto max-w-7xl px-4 py-6 pb-20 text-xs text-muted-foreground sm:px-6 lg:pb-6">
          {pie}
        </div>
      </footer>
    </div>
  );
}
